import { useState } from "react";
import { Link } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { formatJamaicaDate, formatJamaicaDateTime } from "@/lib/datetime";
import {
  Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis,
} from "recharts";
import {
  CheckCircle2, ExternalLink, History, LifeBuoy, Loader2, MoreHorizontal, RefreshCw, Send, XCircle,
} from "lucide-react";
import {
  closeRecoverCase, errorMessage, formatCount, formatMoney, formatMoneyShort, formatRate, getRecover,
  metricsUrls, monthLabel, retryRecoverCharge, sendRecoverLink, syncRecoverCases,
  type RecoverCase, type RecoverResponse,
} from "@/lib/metrics";
import { EmptyState, ErrorState, StatusPill, TableSkeleton, Tile } from "./shared";

const STATUS_TABS = ["open", "recovered", "lost", "closed", "all"] as const;

function invalidateRecover() {
  queryClient.invalidateQueries({
    predicate: (q) => typeof q.queryKey[0] === "string" && (q.queryKey[0] as string).startsWith(`${metricsUrls.recover({ status: "" }).split("?")[0]}`),
  });
}

export default function RecoverTab({ onOpenCustomer }: { onOpenCustomer: (customerId: string) => void }) {
  const { toast } = useToast();
  const [status, setStatus] = useState<string>("open");
  const [linkTarget, setLinkTarget] = useState<RecoverCase | null>(null);
  const [channels, setChannels] = useState<{ email: boolean; whatsapp: boolean }>({ email: true, whatsapp: true });
  const [retryTarget, setRetryTarget] = useState<RecoverCase | null>(null);
  const [closeTarget, setCloseTarget] = useState<RecoverCase | null>(null);
  const [closeReason, setCloseReason] = useState("");

  const { data, isLoading, error } = useQuery<RecoverResponse>({
    queryKey: [metricsUrls.recover({ status })],
    queryFn: () => getRecover(status),
  });

  const syncMutation = useMutation({
    mutationFn: syncRecoverCases,
    onSuccess: (res) => {
      toast({ title: "Cases synced", description: `${res.opened} opened, ${res.recovered} recovered, ${res.lost} written off.` });
      invalidateRecover();
    },
    onError: (e) => toast({ title: "Could not sync cases", description: errorMessage(e), variant: "destructive" }),
  });

  const linkMutation = useMutation({
    mutationFn: ({ id, list }: { id: string; list: string[] }) => sendRecoverLink(id, list),
    onSuccess: (res) => {
      const parts: string[] = [];
      if (res.email) parts.push(res.email.ok ? "email sent" : `email failed (${res.email.error || "unknown"})`);
      if (res.whatsapp) parts.push(res.whatsapp.ok ? "WhatsApp sent" : `WhatsApp failed (${res.whatsapp.error || "unknown"})`);
      const failed = (res.email && !res.email.ok) || (res.whatsapp && !res.whatsapp.ok);
      toast({ title: failed ? "Pay link partly sent" : "Pay link sent", description: parts.join(" · ") || "Nothing to send.", variant: failed ? "destructive" : undefined });
      setLinkTarget(null);
      invalidateRecover();
    },
    onError: (e) => toast({ title: "Could not send the pay link", description: errorMessage(e), variant: "destructive" }),
  });

  const retryMutation = useMutation({
    mutationFn: (id: string) => retryRecoverCharge(id),
    onSuccess: (res) => {
      toast({
        title: res.ok ? "Stripe retried the charge" : "Stripe refused the retry",
        description: res.error || (res.status ? `Invoice is now ${res.status}.` : undefined),
        variant: res.ok ? undefined : "destructive",
      });
      setRetryTarget(null);
      invalidateRecover();
    },
    onError: (e) => toast({ title: "Retry failed", description: errorMessage(e), variant: "destructive" }),
  });

  const closeMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => closeRecoverCase(id, reason),
    onSuccess: () => {
      toast({ title: "Case closed" });
      setCloseTarget(null);
      setCloseReason("");
      invalidateRecover();
    },
    onError: (e) => toast({ title: "Could not close the case", description: errorMessage(e), variant: "destructive" }),
  });

  const stats = data?.stats;
  const cases = data?.cases || [];
  const monthly = (stats?.monthly || []).map((m) => ({
    month: monthLabel(m.month),
    opened: m.opened,
    recovered: m.recovered,
    lost: m.lost,
    recoveredAmount: m.recoveredAmount,
    atRisk: m.atRisk,
  }));

  return (
    <div className="space-y-6" data-testid="tab-recover">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><LifeBuoy className="w-4 h-4" />Failed payment recovery</h2>
          <p className="text-sm text-muted-foreground">Every card that failed, what the dunning sequence has already tried, and what you can do by hand.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending} data-testid="button-sync-cases">
          {syncMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}Sync cases
        </Button>
      </div>

      {isLoading ? <TableSkeleton rows={8} cols={6} />
        : error ? <Card><CardContent className="pt-6"><ErrorState error={error} /></CardContent></Card>
          : !stats ? null : (
            <>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                <Tile
                  label="Open cases"
                  value={formatCount(stats.open.count)}
                  sub={`${formatMoney(stats.open.amount)} at risk`}
                  tone={stats.open.count > 0 ? "warn" : undefined}
                  testId="tile-open-cases"
                />
                <Tile label="Recovered (30 days)" value={formatCount(stats.recovered30.count)} sub={formatMoney(stats.recovered30.amount)} tone={stats.recovered30.count > 0 ? "good" : undefined} />
                <Tile label="Lost (30 days)" value={formatCount(stats.lost30.count)} sub={formatMoney(stats.lost30.amount)} tone={stats.lost30.count > 0 ? "bad" : undefined} />
                <Tile label="90-day recovery rate" value={formatRate(stats.recoveryRate90)} sub="Of cases opened in the last 90 days" />
                <Tile label="Avg days to recover" value={stats.avgDaysToRecover == null ? "–" : stats.avgDaysToRecover.toFixed(1)} sub="From first failure to payment" />
              </div>

              <div className="grid gap-6 lg:grid-cols-3">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">The automated sequence</CardTitle>
                    <CardDescription>This runs on its own for every open case. Manual actions below are on top of it.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {(data?.sequence || []).length === 0 ? (
                      <EmptyState title="No sequence configured" hint="Dunning steps have not been set up on the server yet." />
                    ) : (
                      <ol className="space-y-2">
                        {data!.sequence.map((step, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <span className="rounded-full bg-muted h-5 w-5 shrink-0 text-[11px] flex items-center justify-center font-medium">{i + 1}</span>
                            <span>{step}</span>
                          </li>
                        ))}
                      </ol>
                    )}
                  </CardContent>
                </Card>

                <Card className="lg:col-span-2">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Why the cards failed</CardTitle>
                    <CardDescription>Stripe's decline codes, and how many of each we got back.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {stats.byFailure.length === 0 ? (
                      <EmptyState title="No failures on record" hint="Nothing has been declined, or nothing has been synced yet." />
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Decline code</TableHead>
                              <TableHead className="text-right">Cases</TableHead>
                              <TableHead className="text-right">Amount</TableHead>
                              <TableHead className="text-right">Recovered</TableHead>
                              <TableHead className="text-right">Recovery rate</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {stats.byFailure.map((f) => (
                              <TableRow key={f.code || "unknown"}>
                                <TableCell className="font-mono text-xs">{f.code || "unknown"}</TableCell>
                                <TableCell className="text-right tabular-nums">{formatCount(f.count)}</TableCell>
                                <TableCell className="text-right tabular-nums">{formatMoney(f.amount)}</TableCell>
                                <TableCell className="text-right tabular-nums">{formatCount(f.recovered)}</TableCell>
                                <TableCell className="text-right tabular-nums">{f.count ? `${((f.recovered / f.count) * 100).toFixed(1)}%` : "–"}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Recovery by month</CardTitle>
                  <CardDescription>Cases opened against cases won back, with the money recovered on the right axis.</CardDescription>
                </CardHeader>
                <CardContent>
                  {monthly.length === 0 ? (
                    <EmptyState title="No monthly history" hint="Recovery cases have not been running long enough to draw a trend." />
                  ) : (
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={monthly}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                          <YAxis yAxisId="left" tick={{ fontSize: 11 }} allowDecimals={false} width={40} />
                          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} width={70} tickFormatter={(v: number) => formatMoneyShort(v)} />
                          <ChartTooltip formatter={(v: any, n: any) => [n === "Recovered J$" ? formatMoney(Number(v)) : formatCount(Number(v)), n]} />
                          <Legend />
                          <Bar yAxisId="left" dataKey="opened" name="Opened" fill="hsl(var(--muted-foreground))" isAnimationActive={false} />
                          <Bar yAxisId="left" dataKey="recovered" name="Recovered" fill="#16a34a" isAnimationActive={false} />
                          <Bar yAxisId="left" dataKey="lost" name="Lost" fill="hsl(var(--destructive))" isAnimationActive={false} />
                          <Line yAxisId="right" type="monotone" dataKey="recoveredAmount" name="Recovered J$" stroke="hsl(var(--foreground))" strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-base">Cases</CardTitle>
                      <CardDescription>Send the pay link again, ask Stripe to retry the card, or close a case you have given up on.</CardDescription>
                    </div>
                    <Tabs value={status} onValueChange={setStatus}>
                      <TabsList className="h-9">
                        {STATUS_TABS.map((s) => (
                          <TabsTrigger key={s} value={s} className="capitalize" data-testid={`recover-status-${s}`}>{s}</TabsTrigger>
                        ))}
                      </TabsList>
                    </Tabs>
                  </div>
                </CardHeader>
                <CardContent>
                  {cases.length === 0 ? (
                    <EmptyState
                      title={status === "open" ? "No open cases" : `No ${status} cases`}
                      hint={status === "open"
                        ? "Nothing is failing right now. That is the number you want here."
                        : "Try another status tab, or sync cases to pull the latest from Stripe."}
                    />
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Customer</TableHead>
                            <TableHead>Grade</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                            <TableHead>Failure</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Days open</TableHead>
                            <TableHead>Next reminder</TableHead>
                            <TableHead className="w-32" />
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {cases.map((c) => (
                            <TableRow key={c.id} data-testid={`recover-case-${c.id}`}>
                              <TableCell className="max-w-[16rem]">
                                {c.customerId ? (
                                  <button type="button" className="font-medium text-primary hover:underline text-left truncate block" onClick={() => onOpenCustomer(c.customerId as string)}>
                                    {c.name || c.email || c.customerId}
                                  </button>
                                ) : (
                                  <span className="font-medium">{c.name || c.email || "–"}</span>
                                )}
                                <div className="text-xs text-muted-foreground truncate">{c.email || c.parentEmail || "–"}</div>
                              </TableCell>
                              <TableCell>{c.grade != null ? <Badge variant="outline" className="text-[10px] py-0 h-4">G{c.grade}</Badge> : "–"}</TableCell>
                              <TableCell className="text-right tabular-nums">
                                {c.currency && c.currency.toLowerCase() !== "jmd"
                                  ? `${c.currency.toUpperCase()} ${(c.amount / 100).toFixed(2)}`
                                  : formatMoney(c.amount)}
                              </TableCell>
                              <TableCell className="max-w-[14rem]">
                                <div className="font-mono text-xs">{c.failureCode || "unknown"}</div>
                                {c.failureMessage ? <div className="text-xs text-muted-foreground truncate">{c.failureMessage}</div> : null}
                              </TableCell>
                              <TableCell>
                                <StatusPill status={c.status} />
                                {c.recoveredAt ? <div className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">{formatJamaicaDate(c.recoveredAt)}</div> : null}
                                {c.closedReason ? <div className="text-xs text-muted-foreground mt-0.5 truncate max-w-[10rem]">{c.closedReason}</div> : null}
                              </TableCell>
                              <TableCell className="text-right tabular-nums">{c.daysOpen}</TableCell>
                              <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                                {c.nextReminderAt ? formatJamaicaDate(c.nextReminderAt) : "–"}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center justify-end gap-1">
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Reminder history" data-testid={`reminders-${c.id}`}>
                                        <History className="w-4 h-4" />
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent align="end" className="w-80">
                                      <div className="text-sm font-medium mb-2">Reminders sent</div>
                                      {c.reminders.length === 0 ? (
                                        <p className="text-xs text-muted-foreground">Nothing sent yet. The sequence starts the day the card fails.</p>
                                      ) : (
                                        <ul className="space-y-2 max-h-64 overflow-y-auto">
                                          {c.reminders.map((r, i) => (
                                            <li key={i} className="flex items-start gap-2 text-xs">
                                              {r.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-destructive mt-0.5 shrink-0" />}
                                              <div className="min-w-0">
                                                <div className="font-medium capitalize">Step {r.step} · {r.channel}</div>
                                                <div className="text-muted-foreground">{formatJamaicaDateTime(r.at)}</div>
                                                {r.error ? <div className="text-destructive break-words">{r.error}</div> : null}
                                              </div>
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                    </PopoverContent>
                                  </Popover>

                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`case-menu-${c.id}`}>
                                        <MoreHorizontal className="w-4 h-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-52">
                                      <DropdownMenuItem onClick={() => { setChannels({ email: !!(c.email || c.parentEmail), whatsapp: !!c.parentPhone }); setLinkTarget(c); }}>
                                        <Send className="w-4 h-4 mr-2" />Send pay link
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => setRetryTarget(c)}>
                                        <RefreshCw className="w-4 h-4 mr-2" />Retry charge
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      {c.payUrl && (
                                        <DropdownMenuItem asChild>
                                          <a href={c.payUrl} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4 mr-2" />Open pay link</a>
                                        </DropdownMenuItem>
                                      )}
                                      {c.invoiceId && (
                                        <DropdownMenuItem asChild>
                                          <a href={`https://dashboard.stripe.com/invoices/${c.invoiceId}`} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4 mr-2" />Invoice in Stripe</a>
                                        </DropdownMenuItem>
                                      )}
                                      {c.userId && (
                                        <DropdownMenuItem asChild>
                                          <Link href={`/admin/users?openUser=${c.userId}`}><ExternalLink className="w-4 h-4 mr-2" />Open user record</Link>
                                        </DropdownMenuItem>
                                      )}
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => { setCloseReason(""); setCloseTarget(c); }}>
                                        <XCircle className="w-4 h-4 mr-2" />Close case
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}

      {/* Send pay link */}
      <Dialog open={!!linkTarget} onOpenChange={(open) => { if (!open) setLinkTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send the pay link</DialogTitle>
            <DialogDescription>
              {linkTarget ? `${linkTarget.name || linkTarget.email || "This customer"} owes ${formatMoney(linkTarget.amount)}. Pick where the link should go.` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={channels.email} onCheckedChange={(v) => setChannels((c) => ({ ...c, email: !!v }))} data-testid="checkbox-channel-email" />
              Email {linkTarget?.parentEmail || linkTarget?.email ? <span className="text-muted-foreground text-xs">({linkTarget.parentEmail || linkTarget.email})</span> : <span className="text-destructive text-xs">no address on file</span>}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={channels.whatsapp} onCheckedChange={(v) => setChannels((c) => ({ ...c, whatsapp: !!v }))} data-testid="checkbox-channel-whatsapp" />
              WhatsApp {linkTarget?.parentPhone ? <span className="text-muted-foreground text-xs">({linkTarget.parentPhone})</span> : <span className="text-destructive text-xs">no number on file</span>}
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkTarget(null)}>Cancel</Button>
            <Button
              onClick={() => {
                const list = [channels.email ? "email" : null, channels.whatsapp ? "whatsapp" : null].filter(Boolean) as string[];
                if (!linkTarget || list.length === 0) return;
                linkMutation.mutate({ id: linkTarget.id, list });
              }}
              disabled={linkMutation.isPending || (!channels.email && !channels.whatsapp)}
              data-testid="button-confirm-send-link"
            >
              {linkMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Retry charge */}
      <AlertDialog open={!!retryTarget} onOpenChange={(open) => { if (!open) setRetryTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ask Stripe to charge the card now?</AlertDialogTitle>
            <AlertDialogDescription>
              {retryTarget ? `This attempts ${formatMoney(retryTarget.amount)} against the card on file for ${retryTarget.name || retryTarget.email || "this customer"}. A hard decline may count against the account.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); if (retryTarget) retryMutation.mutate(retryTarget.id); }}
              disabled={retryMutation.isPending}
              data-testid="button-confirm-retry"
            >
              {retryMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Retry charge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Close case */}
      <Dialog open={!!closeTarget} onOpenChange={(open) => { if (!open) setCloseTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Close this case</DialogTitle>
            <DialogDescription>It stops the reminder sequence. Say why, so the number still makes sense in three months.</DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="close-reason" className="text-xs">Reason</Label>
            <Input
              id="close-reason"
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value)}
              placeholder="Paid by bank transfer, wrong number, gave up…"
              data-testid="input-close-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseTarget(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => closeTarget && closeMutation.mutate({ id: closeTarget.id, reason: closeReason.trim() })}
              disabled={closeMutation.isPending || !closeReason.trim()}
              data-testid="button-confirm-close"
            >
              {closeMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Close case
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
